// Consolidated Business Partners page — single sidebar entry that surfaces
// Lenders, Title Companies, and Realtors as PillTabRail tabs. Active tab is
// driven by the URL sub-path (/business-partners/<tab>) so it's deep-linkable.
// V2 re-skin: header, PillTabRail with counts, four KPI tiles, search + sort
// toolbar, and V2 roster rows via VendorListPage.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import VendorListPage from './VendorListPage';
import PillTabRail from '../../ui/PillTabRail';
import { Button, Input, Card } from '../../ui/primitives';
import {
    VENDOR_SORT_OPTIONS,
    applyVendorSearch,
    makeVendorSortComparator,
    buildVendorDealCounts,
    computeMostUsedVendor,
} from './vendorsV2Projection';
import { useLenders } from '../../../store/hooks/useLenders';
import { useTitleCompanies } from '../../../store/hooks/useTitleCompanies';
import { useRealtors } from '../../../store/hooks/useRealtors';
import { useCompanyListConfig } from '../../../store/hooks/useCompanyListConfig';
import useGoogleContactsAvailability from '../../../hooks/useGoogleContactsAvailability';

const TABS = [
    { value: 'lenders',        label: 'Lenders',        slug: 'lenders'        },
    { value: 'titleCompanies', label: 'Title Companies', slug: 'title-companies' },
    { value: 'realtors',       label: 'Realtors',        slug: 'realtors'       },
];

const SLUG_TO_VALUE = TABS.reduce((acc, t) => { acc[t.slug] = t.value; return acc; }, {});
const VALUE_TO_SLUG = TABS.reduce((acc, t) => { acc[t.value] = t.slug; return acc; }, {});

// KPI tile key → activeTab value mapping for emphasis ring
function kpiMatchesTab(kpiKey, activeTab) {
    if (kpiKey === 'lender'  && activeTab === 'lenders')       return true;
    if (kpiKey === 'title'   && activeTab === 'titleCompanies') return true;
    if (kpiKey === 'realtor' && activeTab === 'realtors')      return true;
    return false;
}

// Inline 18px SVG icons (stroke-based, stroke-width 2)
function IconBuilding() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
        </svg>
    );
}
function IconFileCheck() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <polyline points="9 15 11 17 15 13"/>
        </svg>
    );
}
function IconUsers() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
    );
}
function IconStar() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    );
}

function BusinessPartnersPage({
    workspaceId,
    selectedWorkspace = null,
    lenderHandlers,
    titleCompanyHandlers,
    realtorHandlers,
    companyListHandlers,
    vendorModalTrigger = 0,
    realtorLinkedContactsConfig,
    onSearchGoogleContacts,
    allowDelete         = true,
    transactions        = [],
    workspaceName       = '',
}) {
    const location   = useLocation();
    const rrNavigate = useNavigate();

    // D-20 (39-17, Task 1): lenders/titleCompanies/realtors lease per-route
    // via the store instead of the App.js-threaded props; the "add a
    // company" autocomplete configs lease companyLists the same way,
    // replacing the App.js-level lenderCompaniesConfig/
    // titleCompanyCompaniesConfig/realtorCompaniesConfig memos.
    const lenders = useLenders(workspaceId, selectedWorkspace);
    const titleCompanies = useTitleCompanies(workspaceId, selectedWorkspace);
    const realtors = useRealtors(workspaceId, selectedWorkspace);
    const lenderCompaniesConfig = useCompanyListConfig('lenders', workspaceId, selectedWorkspace, companyListHandlers);
    const titleCompanyCompaniesConfig = useCompanyListConfig('titleCompanies', workspaceId, selectedWorkspace, companyListHandlers);
    const realtorCompaniesConfig = useCompanyListConfig('realtors', workspaceId, selectedWorkspace, companyListHandlers);

    const [localTrigger, setLocalTrigger] = React.useState(0);
    const [inputValue,   setInputValue]   = React.useState('');
    const [searchQuery,  setSearchQuery]  = React.useState('');
    const [sortKey,      setSortKey]      = React.useState('recent');

    // Debounced search: 150 ms (mirrors ClientsToolbar.js)
    React.useEffect(() => {
        const timer = setTimeout(() => setSearchQuery(inputValue), 150);
        return () => clearTimeout(timer);
    }, [inputValue]);

    const activeTab = React.useMemo(() => {
        const segment = location.pathname.replace(/^\/business-partners\/?/, '').split('/')[0];
        return SLUG_TO_VALUE[segment] || 'lenders';
    }, [location.pathname]);

    const handleTabChange = React.useCallback((value) => {
        setInputValue('');
        setSearchQuery('');
        rrNavigate(`/business-partners/${VALUE_TO_SLUG[value]}`);
    }, [rrNavigate]);

    // Proactively explain when Google Contacts is unavailable, before any search.
    const { notice: googleContactsNotice, noticeAction: googleContactsNoticeAction } =
        useGoogleContactsAvailability(onSearchGoogleContacts ? workspaceId : null);
    const googleContactsConfig = React.useMemo(
        () => ({
            onSearch: onSearchGoogleContacts,
            unavailableNotice: googleContactsNotice,
            noticeAction: googleContactsNoticeAction,
        }),
        [onSearchGoogleContacts, googleContactsNotice, googleContactsNoticeAction]
    );

    const vendorProps = React.useMemo(() => {
        switch (activeTab) {
            case 'titleCompanies':
                return {
                    key: 'titleCompanies',
                    title: 'Title Companies',
                    entityLabel: 'Title Company',
                    items: titleCompanies,
                    onAdd: titleCompanyHandlers.add,
                    onUpdate: titleCompanyHandlers.update,
                    onDelete: allowDelete ? titleCompanyHandlers.delete : undefined,
                    companiesConfig: titleCompanyCompaniesConfig,
                };
            case 'realtors':
                return {
                    key: 'realtors',
                    title: 'Realtors',
                    entityLabel: 'Realtor',
                    items: realtors,
                    onAdd: realtorHandlers.add,
                    onUpdate: realtorHandlers.update,
                    onDelete: allowDelete ? realtorHandlers.delete : undefined,
                    linkedContactsConfig: realtorLinkedContactsConfig,
                    companiesConfig: realtorCompaniesConfig,
                };
            case 'lenders':
            default:
                return {
                    key: 'lenders',
                    title: 'Lenders',
                    entityLabel: 'Lender',
                    items: lenders,
                    onAdd: lenderHandlers.add,
                    onUpdate: lenderHandlers.update,
                    onDelete: allowDelete ? lenderHandlers.delete : undefined,
                    companiesConfig: lenderCompaniesConfig,
                };
        }
    }, [
        activeTab,
        allowDelete,
        lenders, lenderHandlers, lenderCompaniesConfig,
        titleCompanies, titleCompanyHandlers, titleCompanyCompaniesConfig,
        realtors, realtorHandlers, realtorLinkedContactsConfig, realtorCompaniesConfig,
    ]);

    // Single projection useMemo — KPIs over full lists; search+sort over active tab only
    const { dealCounts, lenderCount, titleCount, realtorCount, mostUsed, filteredSorted } =
        React.useMemo(() => {
            const dc = buildVendorDealCounts(transactions);

            const lc = lenders.length;
            const tc = titleCompanies.length;
            const rc = realtors.length;
            const mu = computeMostUsedVendor([...lenders, ...titleCompanies, ...realtors], dc);

            const activeItems = activeTab === 'titleCompanies' ? titleCompanies
                : activeTab === 'realtors' ? realtors
                : lenders;

            const searched = applyVendorSearch(activeItems, searchQuery);
            const sorted   = [...searched].sort(makeVendorSortComparator(sortKey, dc));

            return {
                dealCounts:    dc,
                lenderCount:   lc,
                titleCount:    tc,
                realtorCount:  rc,
                mostUsed:      mu,
                filteredSorted: sorted,
            };
        }, [transactions, lenders, titleCompanies, realtors, activeTab, searchQuery, sortKey]);

    // PillTabRail tabs with per-type counts in labels
    const tabsWithCounts = React.useMemo(() => [
        { value: 'lenders',        label: `Lenders ${lenderCount}`,         slug: 'lenders'        },
        { value: 'titleCompanies', label: `Title Companies ${titleCount}`,   slug: 'title-companies' },
        { value: 'realtors',       label: `Realtors ${realtorCount}`,        slug: 'realtors'       },
    ], [lenderCount, titleCount, realtorCount]);

    // Context-aware "+ Add" label
    const addLabel = `+ Add ${vendorProps.entityLabel}`;

    // Plural labels for search placeholder + roster heading
    const pluralMap = { lenders: 'lenders', titleCompanies: 'title companies', realtors: 'realtors' };
    const pluralLabel = pluralMap[activeTab] || 'partners';

    // KPI tile emphasis helper
    const kpiStyle = (kpiKey) => ({
        outline: kpiMatchesTab(kpiKey, activeTab) ? '2px solid var(--color-accent)' : 'none',
        outlineOffset: '-1px',
    });

    return (
        <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <header data-tour="partners-header" style={{ animationDelay: '0.02s' }}>
                <div className="text-xs font-medium uppercase tracking-wide text-[color:var(--text-muted)]">
                    {workspaceName ? `Workspace · ${workspaceName}` : 'Workspace'}
                </div>
                <h1
                    className="text-2xl font-semibold tracking-[-0.02em] text-[color:var(--text-primary)]"
                    style={{ marginTop: 6 }}
                >
                    Business Partners
                </h1>
                <p
                    className="text-sm text-[color:var(--text-muted)]"
                    style={{ marginTop: 4, maxWidth: '72ch' }}
                >
                    Your trusted lenders, title companies, and realtors — the people you bring to every deal.
                </p>
            </header>

            {/* Rail row: PillTabRail + Add CTA */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 22,
                    marginBottom: 16,
                    animationDelay: '0.06s',
                }}
            >
                <PillTabRail
                    tabs={tabsWithCounts}
                    activeValue={activeTab}
                    onChange={handleTabChange}
                    ariaLabel="Business partner type"
                />
                <Button
                    data-tour="partners-add"
                    variant="accent"
                    onClick={() => setLocalTrigger(t => t + 1)}
                >
                    {addLabel}
                </Button>
            </div>

            {/* KPI tiles */}
            <section
                className="grid grid-cols-2 gap-4 md:grid-cols-4"
                style={{ marginBottom: 22 }}
            >
                {/* Lenders tile — clickable, navigates to Lenders tab */}
                <button
                    type="button"
                    aria-label="View Lenders"
                    onClick={() => handleTabChange('lenders')}
                    className="text-left w-full cursor-pointer hover:bg-[color:var(--surface-hover)] rounded-xl transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/40"
                >
                    <Card
                        padding="none"
                        data-kpi="lender"
                        style={{ padding: '18px 20px', animationDelay: '0.10s', ...kpiStyle('lender') }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <span
                                className="inline-flex items-center justify-center rounded-lg bg-[color:var(--surface-info)] text-[color:var(--action-link-color)]"
                                style={{ width: 40, height: 40, flexShrink: 0 }}
                            >
                                <IconBuilding />
                            </span>
                            <span className="text-xs font-medium text-[color:var(--text-muted)]">
                                Lenders
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: 34, lineHeight: 1, letterSpacing: '-0.04em',
                                fontWeight: 700, color: 'var(--text-primary)',
                                fontFamily: 'var(--font-display, var(--font-sans))',
                            }}
                        >
                            {lenderCount}
                        </div>
                        <div className="text-[12px]" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                            Lending partners
                        </div>
                    </Card>
                </button>

                {/* Title Companies tile — clickable, navigates to Title Companies tab */}
                <button
                    type="button"
                    aria-label="View Title Companies"
                    onClick={() => handleTabChange('titleCompanies')}
                    className="text-left w-full cursor-pointer hover:bg-[color:var(--surface-hover)] rounded-xl transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/40"
                >
                    <Card
                        padding="none"
                        data-kpi="title"
                        style={{ padding: '18px 20px', animationDelay: '0.14s', ...kpiStyle('title') }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <span
                                className="inline-flex items-center justify-center rounded-lg bg-[color:var(--surface-success)] text-[color:var(--financial-action-color)]"
                                style={{ width: 40, height: 40, flexShrink: 0 }}
                            >
                                <IconFileCheck />
                            </span>
                            <span className="text-xs font-medium text-[color:var(--text-muted)]">
                                Title companies
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: 34, lineHeight: 1, letterSpacing: '-0.04em',
                                fontWeight: 700, color: 'var(--text-primary)',
                                fontFamily: 'var(--font-display, var(--font-sans))',
                            }}
                        >
                            {titleCount}
                        </div>
                        <div className="text-[12px]" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                            Closing partners
                        </div>
                    </Card>
                </button>

                {/* Realtors tile — clickable, navigates to Realtors tab */}
                <button
                    type="button"
                    aria-label="View Realtors"
                    onClick={() => handleTabChange('realtors')}
                    className="text-left w-full cursor-pointer hover:bg-[color:var(--surface-hover)] rounded-xl transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/40"
                >
                    <Card
                        padding="none"
                        data-kpi="realtor"
                        style={{ padding: '18px 20px', animationDelay: '0.18s', ...kpiStyle('realtor') }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <span
                                className="inline-flex items-center justify-center rounded-lg bg-[color:var(--surface-warning)] text-[color:var(--color-accent)]"
                                style={{ width: 40, height: 40, flexShrink: 0 }}
                            >
                                <IconUsers />
                            </span>
                            <span className="text-xs font-medium text-[color:var(--text-muted)]">
                                Realtors
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: 34, lineHeight: 1, letterSpacing: '-0.04em',
                                fontWeight: 700, color: 'var(--text-primary)',
                                fontFamily: 'var(--font-display, var(--font-sans))',
                            }}
                        >
                            {realtorCount}
                        </div>
                        <div className="text-[12px]" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                            Co-op contacts
                        </div>
                    </Card>
                </button>

                {/* Most-used tile */}
                <Card
                    padding="none"
                    style={{ padding: '18px 20px', animationDelay: '0.22s' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <span
                            className="inline-flex items-center justify-center rounded-lg bg-[color:var(--surface-warning)] text-[color:var(--color-accent)]"
                            style={{ width: 40, height: 40, flexShrink: 0 }}
                        >
                            <IconStar />
                        </span>
                        <span className="text-xs font-medium text-[color:var(--text-muted)]">
                            Most-used
                        </span>
                    </div>
                    <div
                        style={{
                            fontSize: 20, lineHeight: 1, fontWeight: 600,
                            color: 'var(--text-primary)',
                        }}
                    >
                        {mostUsed ? mostUsed.name || '—' : '—'}
                    </div>
                    <div className="text-[12px]" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                        {mostUsed ? `${mostUsed.count} deals` : '0 deals'}
                    </div>
                </Card>
            </section>

            {/* Toolbar: search + sort */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 22,
                    flexWrap: 'wrap',
                    animationDelay: '0.26s',
                }}
            >
                <Input
                    type="text"
                    placeholder={`Search ${pluralLabel}…`}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    style={{ flex: '1 1 320px' }}
                />
                <select
                    value={sortKey}
                    onChange={e => setSortKey(e.target.value)}
                    className="rounded-lg px-3 py-2 text-sm bg-[color:var(--surface-primary)] text-[color:var(--text-primary)] border border-[color:var(--border-color)] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors duration-150"
                    style={{ minWidth: 168 }}
                >
                    {VENDOR_SORT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            {/* Roster heading */}
            <div
                className="text-base font-semibold text-[color:var(--text-primary)]"
                style={{ marginBottom: 12 }}
            >
                All {pluralLabel}
            </div>

            {/* Vendor list */}
            <VendorListPage
                {...vendorProps}
                items={filteredSorted}
                allItems={vendorProps.items}
                dealCounts={dealCounts}
                searchQuery={searchQuery}
                modalTrigger={vendorModalTrigger + localTrigger}
                googleContactsConfig={googleContactsConfig}
                hideHeader
            />
        </div>
    );
}

export default BusinessPartnersPage;
